import { motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Edit3, Ship, Zap, Settings2, DollarSign, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

export const StepRecap = ({ tr, projectType, contact, operational, energy, technical, budget, onConfirm, onEdit }) => {
  
  // Calculate uncertainty flags
  const uncertainties = [];
  
  // Check shore power
  if (energy.shore_charging_possible === null) {
    uncertainties.push({ key: 'shore_power_unknown', step: 4 });
  }
  
  // Check power capacity
  if (energy.shore_charging_possible && energy.power_unknown) {
    uncertainties.push({ key: 'power_capacity_unknown', step: 4 });
  }
  
  // Check operational data completeness
  if (!operational.trips_per_day || !operational.trip_duration_minutes) {
    uncertainties.push({ key: 'operational_data_incomplete', step: 3 });
  }
  
  // Check high autonomy with limited charging
  if (operational.trips_per_day && operational.trip_duration_minutes && energy.charging_window_hours) {
    const dailyHours = (operational.trips_per_day * operational.trip_duration_minutes) / 60;
    if (dailyHours > 6 && energy.charging_window_hours < 4) {
      uncertainties.push({ key: 'high_autonomy_limited_charging', step: 4 });
    }
  }
  
  // Check budget
  if (!budget.budget_range || budget.budget_range === 'unknown') {
    uncertainties.push({ key: 'budget_not_provided', step: 6 });
  }
  
  // Check timeline
  if (!budget.decision_timeline || budget.decision_timeline === 'unknown') {
    uncertainties.push({ key: 'timeline_not_provided', step: 6 });
  }
  
  // Check engine power for retrofit
  if ((projectType === 'retrofit' || projectType === 'hybrid') && technical.engine_power_unknown) {
    uncertainties.push({ key: 'engine_power_unknown', step: 5 });
  }
  
  // Check speed/distance consistency
  if (operational.cruise_speed_knots && operational.trip_distance_nm && operational.trip_duration_minutes) {
    const expectedDuration = (operational.trip_distance_nm / operational.cruise_speed_knots) * 60;
    if (Math.abs(expectedDuration - operational.trip_duration_minutes) > 15) {
      uncertainties.push({ key: 'inconsistent_speed_distance', step: 3 });
    }
  }

  const getProjectTypeLabel = () => {
    const types = {
      retrofit: tr.projectType.retrofit,
      new_build: tr.projectType.newBuild,
      hybrid: tr.projectType.hybrid,
      shore_power: tr.projectType.shorePower,
      battery_only: tr.projectType.batteryOnly,
      feasibility: tr.projectType.feasibility
    };
    return types[projectType] || projectType;
  };

  const RecapSection = ({ title, icon: Icon, children, stepNumber }) => (
    <Card className="bg-white">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Icon className="w-5 h-5 text-[#0B2A4A]" />
            {title}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => onEdit(stepNumber)} className="text-[#0B2A4A] hover:text-[#0B2A4A]/80">
            <Edit3 className="w-4 h-4 mr-1" /> 
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );

  const DataRow = ({ label, value, missing }) => (
    <div className="flex justify-between py-1">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className={`text-sm font-medium ${missing ? 'text-amber-600 italic' : ''}`}>
        {missing ? tr.recap.notProvided : value}
      </span>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-[#0B2A4A] font-['Tomorrow'] mb-2">{tr.recap.title}</h2>
        <p className="text-muted-foreground">{tr.recap.subtitle}</p>
      </div>

      {/* Uncertainty Flags */}
      {uncertainties.length > 0 ? (
        <Card className="border-amber-300 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-800">
              <AlertCircle className="w-5 h-5" />
              {tr.recap.uncertainties}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {uncertainties.map((u, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span className="text-amber-700">{tr.uncertaintyFlags[u.key]}</span>
                  <Button variant="ghost" size="sm" onClick={() => onEdit(u.step)} className="text-amber-700 hover:text-amber-900 h-7 px-2">
                    <Edit3 className="w-3 h-3 mr-1" /> Edit
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-green-300 bg-green-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-medium">{tr.recap.noUncertainties}</span>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {/* Project Type */}
        <RecapSection title={tr.recap.projectTypeSection} icon={Ship} stepNumber={2}>
          <Badge variant="secondary" className="text-base py-1 px-3 bg-[#0B2A4A]/10 text-[#0B2A4A]">
            {getProjectTypeLabel()}
          </Badge>
        </RecapSection>

        {/* Budget & Timeline */}
        <RecapSection title={tr.recap.budgetSection} icon={DollarSign} stepNumber={6}>
          <DataRow label={tr.budget.budgetRange} value={tr.budgets[budget.budget_range]} missing={!budget.budget_range || budget.budget_range === 'unknown'} />
          <DataRow label={tr.budget.timeline} value={tr.timelines[budget.decision_timeline]} missing={!budget.decision_timeline || budget.decision_timeline === 'unknown'} />
        </RecapSection>
      </div>

      {/* Operational Profile */}
      <RecapSection title={tr.recap.operationalSection} icon={Settings2} stepNumber={3}>
        {operational.usage_description && (
          <p className="text-sm mb-3 p-2 bg-muted rounded">{operational.usage_description}</p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div className="p-2 bg-muted rounded">
            <p className="text-lg font-bold text-[#0B2A4A]">{operational.trips_per_day || '—'}</p>
            <p className="text-xs text-muted-foreground">{tr.recap.trips}</p>
          </div>
          <div className="p-2 bg-muted rounded">
            <p className="text-lg font-bold text-[#0B2A4A]">{operational.trip_duration_minutes || '—'}</p>
            <p className="text-xs text-muted-foreground">{tr.recap.duration}</p>
          </div>
          <div className="p-2 bg-muted rounded">
            <p className="text-lg font-bold text-[#0B2A4A]">{operational.trip_distance_nm || '—'}</p>
            <p className="text-xs text-muted-foreground">{tr.recap.distance}</p>
          </div>
          <div className="p-2 bg-muted rounded">
            <p className="text-lg font-bold text-[#0B2A4A]">{operational.cruise_speed_knots || '—'}</p>
            <p className="text-xs text-muted-foreground">{tr.recap.speed}</p>
          </div>
          <div className="p-2 bg-muted rounded">
            <p className="text-lg font-bold text-[#0B2A4A]">{operational.dock_time_minutes || '—'}</p>
            <p className="text-xs text-muted-foreground">{tr.recap.dockTime}</p>
          </div>
        </div>
      </RecapSection>

      {/* Energy & Charging */}
      <RecapSection title={tr.recap.energySection} icon={Zap} stepNumber={4}>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            {energy.shore_charging_possible === true && (
              <Badge className="bg-green-100 text-green-800 hover:bg-green-100">{tr.recap.shoreChargingYes}</Badge>
            )}
            {energy.shore_charging_possible === false && (
              <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{tr.recap.shoreChargingNo}</Badge>
            )}
            {(energy.shore_charging_possible === null || energy.shore_charging_possible === 'unknown') && (
              <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{tr.recap.shoreChargingUnknown}</Badge>
            )}
          </div>
          {energy.shore_charging_possible === true && (
            <div className="space-y-2 mt-2">
              {/* Connection Type */}
              {energy.phase_type && (
                <DataRow 
                  label={tr.energy.phaseType} 
                  value={energy.phase_type === 'single' ? tr.energy.singlePhase : tr.energy.threePhase}
                />
              )}
              {/* Amperage */}
              {(energy.amperage || energy.power_unknown) && (
                <DataRow 
                  label={tr.energy.amperageAvailable} 
                  value={
                    energy.power_unknown 
                      ? tr.energy.powerUnknown 
                      : energy.amperage === 'other' && energy.amperage_other 
                        ? `${energy.amperage_other} A` 
                        : energy.amperage !== 'other' 
                          ? `${energy.amperage} A` 
                          : null
                  }
                  missing={!energy.amperage && !energy.power_unknown}
                />
              )}
              {/* Charging Window */}
              <DataRow 
                label={tr.recap.chargingHours} 
                value={energy.charging_window_hours ? `${energy.charging_window_hours}h` : null}
                missing={!energy.charging_window_hours}
              />
              {/* Charging Points */}
              {energy.charging_points && (
                <DataRow 
                  label={tr.energy.chargingPoints} 
                  value={energy.charging_points}
                />
              )}
            </div>
          )}
        </div>
      </RecapSection>

      <Separator />

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button variant="outline" size="lg" onClick={() => onEdit(1)} className="flex items-center gap-2">
          <Edit3 className="w-4 h-4" />
          {tr.recap.adjustAnswers}
        </Button>
        <Button size="lg" onClick={onConfirm} className="bg-[#0B2A4A] hover:bg-[#0B2A4A]/90 flex items-center gap-2">
          {tr.recap.confirmCorrect}
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};
